/**
 * Named numeric constants for the uploader pipeline (preflight bounds, multipart
 * sizing, the XLSX size guard). Isomorphic — both the browser pipeline and the
 * Worker relay reference these so the two never drift on a magic number.
 *
 * Values follow the client-pipeline design doc (§3). The R2 multipart minimum
 * part size is 5 MB (all parts except the last); PART_SIZE_BYTES sits above that
 * for good throughput while keeping per-request Worker memory bounded.
 */

/** Rows read in the bounded preflight sample — enough for a stable PII ratio. */
export const PREFLIGHT_SAMPLE_ROWS = 200;

/**
 * Upper byte bound on the preflight CSV slice. A safety cap so a pathological
 * single-line file can't blow memory; the row `preview` stops parsing first.
 */
export const PREFLIGHT_SLICE_BYTES = 256 * 1024;

/**
 * XLSX size guard. SheetJS cannot stream a partial slice — it materializes the
 * whole workbook — so files over this size are nudged to CSV before they ever
 * reach the parser.
 */
export const XLSX_SIZE_GUARD_BYTES = 50 * 1024 * 1024;

/**
 * Multipart part size for the scrubbed upload. Above R2's 5 MB multipart minimum
 * (which applies to every part except the last) and bounds the in-flight buffer.
 */
export const PART_SIZE_BYTES = 8 * 1024 * 1024;

/**
 * Sanity cap on a single scrubbed upload. A ~1M-row scrubbed CSV is far under
 * this; the cap exists to reject obviously-wrong inputs, not to constrain real
 * portfolios.
 */
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * Per-upload budget for XLSX files. XLSX cannot be streamed — SheetJS materializes
 * the whole workbook in the BROWSER (see client/uploader/parse-stream.ts), so a
 * batch of many or large Excel files spikes the browser tab's memory. The server
 * never parses XLSX (it ingests only already-scrubbed CSV), so this is purely a
 * client-side guard, checked before any workbook is read.
 *
 * XLSX is the small-file exception (CSV is the path for 500k-row portfolios), so
 * these are deliberately conservative: 5 files covers the three declared file types
 * with headroom, and 100 MB aggregate admits a couple of medium workbooks or a
 * handful of small ones while keeping peak memory bounded. Individual files stay
 * capped at XLSX_SIZE_GUARD_BYTES. Over either limit the UI nudges the user to
 * export to CSV or split into separate uploads.
 */
export const MAX_XLSX_FILES_PER_UPLOAD = 5;
export const MAX_XLSX_AGGREGATE_BYTES = 100 * 1024 * 1024;

/**
 * Row-count warning threshold for XLSX. Distinct from the byte-based guards
 * above: a workbook can sit comfortably under `XLSX_SIZE_GUARD_BYTES` (dense,
 * few columns) yet still hold enough ROWS that the whole-workbook memory model
 * (SheetJS materializes every row, not just the preflight sample) makes it a
 * poor fit for the 500k-row bulk/backfill target. Past this row count the
 * add-time notice steers the user to CSV (streamed, constant memory) instead of
 * rejecting the file outright; a bulk/backfill upload should always prefer CSV.
 * Chosen well under the 500k-row target so the warning fires long before a file
 * is large enough to matter at that scale.
 */
export const XLSX_ROW_WARNING_THRESHOLD = 100_000;

// ── Terminal-transfer retention ──────────────────────────────────────────────
//
// An `upload_transfers` row tracks one in-flight multipart-upload SESSION. Once it
// reaches a terminal status (`completed` — its scrubbed object now backs an
// UploadFile — or `aborted`), it is bookkeeping residue and is pruned by the
// upload-cleanup cron after this short retention window (kept briefly for
// post-mortem/debugging of a just-finished transfer, not long-term). Server-only
// concern (no client/API surface reads it), but colocated with the draft-retention
// windows since the same cron owns both.

/** Hours a TERMINAL (completed/aborted) transfer row is retained before pruning. */
export const TERMINAL_TRANSFER_RETENTION_HOURS = 24;

export const TERMINAL_TRANSFER_RETENTION_MS = TERMINAL_TRANSFER_RETENTION_HOURS * 60 * 60 * 1000;

/** Max terminal transfer rows pruned per cron tick (bounds a tick's delete work). */
export const TERMINAL_TRANSFER_PRUNE_LIMIT = 5000;

// ── Soft-deleted ledger-payload retention (C6.3) ─────────────────────────────
//
// The row-grain ledger (`upload_source_rows`) keeps ACTIVE data forever (it is the
// recompute basis). When an upload/file is SOFT-DELETED, its rows stay as metadata
// but their cell PAYLOAD — the four JSONB maps (`cells` / `blankCells` /
// `parsedNumerics` / `parsedDates`) — is dropped this many days after deletion by the
// upload-cleanup cron (payload nulled, `payloadDroppedAt` stamped; the row metadata +
// protected ids + the scrubbed R2 object are kept). Applies equally to rows soft-deleted
// while still draft. The partial index `upload_source_rows_retention_sweep_idx` serves the
// bounded, cross-org selection. Server-only concern; colocated with the transfer window
// above since the same cron owns both.

/** Days a SOFT-DELETED upload's ledger cell payload is retained before it is dropped. */
export const SOFT_DELETED_LEDGER_PAYLOAD_RETENTION_DAYS = 90;

export const SOFT_DELETED_LEDGER_PAYLOAD_RETENTION_MS =
  SOFT_DELETED_LEDGER_PAYLOAD_RETENTION_DAYS * 24 * 60 * 60 * 1000;

/** Rows whose payload is nulled per chunked UPDATE (bounds one statement's write work). */
export const LEDGER_PAYLOAD_DROP_CHUNK = 10_000;

/** Max rows a single cron tick will sweep (caps the runner's per-tick chunk loop). */
export const LEDGER_PAYLOAD_DROP_MAX_PER_TICK = 100_000;
